/**
 * API bibliothèque de templates mockup (S4).
 *
 * CRUD + tags manuels + relaunch Vision + preview à la volée + matching
 * publication. Le POST create utilise multipart/form-data (image + JSON
 * form fields encodés). Preview renvoie un blob `image/webp` à afficher
 * via `URL.createObjectURL`.
 */

import { apiClient } from './client'
import type { TagResponse } from '@/types/works'
import type {
  BrowseForWorkParams,
  ListTemplatesParams,
  MatchForWorkParams,
  MockupTemplateAttachTagRequest,
  MockupTemplateCreatePayload,
  MockupTemplateMatchResponse,
  MockupTemplateResponse,
  MockupTemplateSummaryResponse,
  MockupTemplateUpdateRequest,
} from '@/types/mockup-templates'

export const mockupTemplatesApi = {
  list: async (params: ListTemplatesParams = {}): Promise<MockupTemplateSummaryResponse[]> => {
    const { data } = await apiClient.get<MockupTemplateSummaryResponse[]>('/mockup-templates', {
      params,
    })
    return data
  },

  get: async (id: number): Promise<MockupTemplateResponse> => {
    const { data } = await apiClient.get<MockupTemplateResponse>(`/mockup-templates/${id}`)
    return data
  },

  create: async (payload: MockupTemplateCreatePayload): Promise<MockupTemplateResponse> => {
    const form = new FormData()
    form.append('file', payload.file)
    // Si `name` est absent (drag-drop bulk), on omet le champ ; le BE
    // dérive depuis `file.filename`. Cf type pour le détail.
    if (payload.name) form.append('name', payload.name)
    form.append('kind', payload.kind)
    if (payload.slug) form.append('slug', payload.slug)
    if (payload.notes) form.append('notes', payload.notes)
    if (payload.mask_zone) {
      form.append('mask_zone', JSON.stringify(payload.mask_zone))
    }
    if (payload.initial_tag_slugs) {
      form.append('initial_tag_slugs', JSON.stringify(payload.initial_tag_slugs))
    }
    const { data } = await apiClient.post<MockupTemplateResponse>('/mockup-templates', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // 5 min : le default 30s d'`apiClient` est insuffisant pour
      // l'upload multipart d'un PSD lourd (50-150 Mo) sur un lien
      // client modeste — surtout en concurrence (modal drag-drop
      // envoie N requêtes simultanées, chacune partage la bande
      // passante upload du client). Cf `works.ts` qui a 120s pour
      // les RAW. Côté BE, `asyncio.to_thread` + ~1.5s de traitement
      // ne dépasse jamais 5 s — c'est le réseau client→serveur
      // qui dicte le timeout.
      timeout: 300_000,
    })
    return data
  },

  update: async (
    id: number,
    payload: MockupTemplateUpdateRequest,
  ): Promise<MockupTemplateResponse> => {
    const { data } = await apiClient.patch<MockupTemplateResponse>(
      `/mockup-templates/${id}`,
      payload,
    )
    return data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/mockup-templates/${id}`)
  },

  attachTag: async (id: number, payload: MockupTemplateAttachTagRequest): Promise<TagResponse> => {
    const { data } = await apiClient.post<TagResponse>(`/mockup-templates/${id}/tags`, payload)
    return data
  },

  detachTag: async (id: number, tagId: number): Promise<void> => {
    await apiClient.delete(`/mockup-templates/${id}/tags/${tagId}`)
  },

  /**
   * Réarme le job Vision. `sync=true` attend le résultat (~3-6s) et
   * renvoie le template avec descripteurs/tags à jour. `sync=false`
   * (défaut) lance en BackgroundTasks et renvoie immédiatement —
   * le client poll le détail du template pour `vision_extracted_at`.
   */
  relaunchVision: async (
    id: number,
    sync = false,
  ): Promise<{ status: string; template?: MockupTemplateResponse }> => {
    const { data } = await apiClient.post<{
      status: string
      template?: MockupTemplateResponse
    }>(`/mockup-templates/${id}/relaunch-vision`, null, { params: { sync } })
    return data
  },

  /**
   * Génère un mockup composité à la volée et renvoie un blob `image/webp`.
   * Pas de persistance MinIO — le client utilise `URL.createObjectURL` pour
   * afficher dans un `<img>`.
   */
  preview: async (id: number, workId: number): Promise<Blob> => {
    const { data } = await apiClient.post<Blob>(`/mockup-templates/${id}/preview`, null, {
      params: { work_id: workId },
      responseType: 'blob',
    })
    return data
  },

  matchForWork: async (params: MatchForWorkParams): Promise<MockupTemplateMatchResponse[]> => {
    const { data } = await apiClient.get<MockupTemplateMatchResponse[]>(
      '/mockup-templates/match/for-work',
      { params },
    )
    return data
  },

  /**
   * Liste TOUS les templates compatibles format pour une œuvre, triés par
   * qualité décroissante (composition + palette + rotation + freshness).
   *
   * Mode picker manuel — pas de cap de couverture, pas d'exclusion sticky.
   * Contrairement à `matchForWork` qui retourne une séquence de listing
   * Etsy contrainte (~10 items max via la grammaire de couverture), ce mode
   * alimente le `<MockupTemplatePicker>` pour que Stéphanie voie tout le
   * catalogue compatible et choisisse elle-même.
   */
  browseForWork: async (params: BrowseForWorkParams): Promise<MockupTemplateMatchResponse[]> => {
    const { data } = await apiClient.get<MockupTemplateMatchResponse[]>(
      '/mockup-templates/browse/for-work',
      { params },
    )
    return data
  },
}
